import { AlertTriangle, PackageSearch } from "lucide-react";
import Link from "next/link";
import { useEffect } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

/** `app/error.tsx`. React Router's `errorElement` gives us `error` + `reset`. */
export function ErrorBoundaryPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  const message =
    error instanceof Error
      ? error.message
      : isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}`
        : "";

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-5 py-24 text-center">
      <AlertTriangle className="h-16 w-16 text-amazon-red" />
      <h1 className="mt-6 text-3xl font-black tracking-normal text-slate-950 dark:text-white">
        Something went wrong
      </h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">
        An unexpected error occurred while loading this page. You can try again or head back to the homepage.
      </p>
      {import.meta.env.DEV && message && (
        <pre className="mt-4 max-w-full overflow-x-auto rounded bg-red-50 p-3 text-left text-xs font-mono text-red-600 dark:bg-red-950/30 dark:text-red-400">
          {message}
        </pre>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button type="button" onClick={() => navigate(0)} className="amazon-btn-primary inline-flex h-11 items-center px-5">
          Try again
        </button>
        <Link
          href="/"
          className="inline-flex h-11 items-center rounded-full border border-slate-300 px-5 text-sm font-bold text-slate-800 hover:bg-slate-50 dark:border-white/20 dark:text-slate-100 dark:hover:bg-white/5"
        >
          Go to homepage
        </Link>
      </div>
    </div>
  );
}

/** `app/not-found.tsx`. */
export function NotFoundPage() {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-5 py-24 text-center">
      <PackageSearch className="h-16 w-16 text-amazon-orange" />
      <h1 className="mt-6 text-3xl font-black tracking-normal text-slate-950 dark:text-white">
        We couldn&apos;t find that page
      </h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">
        The page you&apos;re looking for may have been moved or doesn&apos;t exist. Let&apos;s get you back to shopping.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link href="/" className="amazon-btn-primary inline-flex h-11 items-center px-5">
          Go to homepage
        </Link>
        <Link
          href="/search"
          className="inline-flex h-11 items-center rounded-full border border-slate-300 px-5 text-sm font-bold text-slate-800 hover:bg-slate-50 dark:border-white/20 dark:text-slate-100 dark:hover:bg-white/5"
        >
          Browse products
        </Link>
      </div>
    </div>
  );
}